/**
 * ApiKeysTable Component
 *
 * Displays a table of API keys with their roles, role editing and revoke actions.
 * Responsive design that works on mobile and desktop.
 */
import { Temporal } from '@insio/dates';
import { Badge, Button, Icon, SimpleTable } from '@insio/ui-toolkit';
import type { ApiKeyFieldsFragment } from '@/graphql';
import { MEMBER_ROLE, getSingleRoleBadgeColor } from '@/features/admin/constants/roles';
import { useEnumTranslation } from '@/hooks/useEnumTranslation';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useFormatShortDate } from '@/hooks/use-date-formatters';
import { RevokeApiKeyButton } from './RevokeApiKeyButton';

type ApiKey = ApiKeyFieldsFragment;

interface ApiKeysTableProps {
  keys: ApiKey[];
  loading: boolean;
  error?: unknown;
  onRevoked?: () => void;
  onEditRoles: (key: ApiKey) => void;
}

export function ApiKeysTable({ keys, loading, error, onRevoked, onEditRoles }: ApiKeysTableProps) {
  const t = useFeatureTranslation('systemSettings.apiKeys');
  const tRole = useEnumTranslation('userRole');
  const formatShortDate = useFormatShortDate();

  const formatDate = (date: Temporal.PlainDate | Temporal.Instant | null | undefined) => {
    if (!date) return t('table.never');
    return formatShortDate(date);
  };

  if (loading && keys.length === 0) {
    return (
      <div className='py-xl text-center'>
        <p className='text-sm text-foreground-muted'>{t('table.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='py-xl text-center'>
        <p className='text-sm text-error-text'>{t('table.error')}</p>
      </div>
    );
  }

  const headers = [
    t('fields.name'),
    t('fields.key'),
    t('fields.roles'),
    t('fields.createdAt'),
    t('fields.lastUsedAt'),
    t('fields.expiresAt'),
    t('fields.rateLimit'),
    t('fields.status'),
    <span key='actions-header' className='flex justify-center'>
      {t('fields.actions')}
    </span>
  ];

  return (
    <SimpleTable<ApiKey>
      headers={headers}
      data={keys}
      scrollable
      NoContentFallback={<p className='text-sm text-foreground-muted'>{t('table.empty')}</p>}
      renderRow={key => [
        <span key='name' className='text-xs md:text-sm font-medium text-foreground-base'>
          {key.name}
        </span>,
        <code key='key' className='text-xs bg-background-input px-sm py-xs rounded font-mono'>
          {key.keyPrefix}...
        </code>,
        <div key='roles' className='flex flex-wrap gap-xs'>
          {(key.roles.length > 0 ? key.roles : [MEMBER_ROLE]).map(role => (
            <Badge key={role} color={getSingleRoleBadgeColor(role)} size='sm'>
              {tRole(role)}
            </Badge>
          ))}
        </div>,
        <span key='created' className='text-xs md:text-sm text-foreground-base'>
          {formatDate(key.createdAt)}
        </span>,
        <span key='lastUsed' className='text-xs md:text-sm text-foreground-base'>
          {formatDate(key.lastUsedAt)}
        </span>,
        <span key='expires' className='text-xs md:text-sm text-foreground-base'>
          {formatDate(key.expiresAt)}
        </span>,
        <span key='rateLimit' className='text-xs md:text-sm text-foreground-base'>
          {key.rateLimitPerMin}/min
        </span>,
        <Badge key='status' color={key.isRevoked ? 'error' : 'success'} size='sm'>
          {key.isRevoked ? t('status.revoked') : t('status.active')}
        </Badge>,
        <div key='actions' className='flex justify-center gap-xs'>
          {!key.isRevoked && key.rolesEditable && (
            <Button
              variant='ghost'
              size='sm'
              onClick={() => onEditRoles(key)}
              aria-label={t('actions.editRoles')}
              testId={`api-key-edit-roles-btn-${key.id}`}
            >
              <Icon name='pen' size='sm' />
            </Button>
          )}
          {!key.isRevoked && <RevokeApiKeyButton keyId={key.id} onRevoked={onRevoked} />}
        </div>
      ]}
    />
  );
}
